import { useKeyboardInset } from "@/components/ui/drawer.tsx";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog.tsx";
import { cn, getApiOrigin } from "@/lib/utils.ts";
import {
  browserWindowAction,
  isBrowserWindowUrl,
  browserConnectionFromWindow,
  browserConnectionEvent,
  browserWindowConnectionAttempt,
  browserWindowAttemptFromUrl,
  recoverBrowserWindowDocument,
  browserWindowReady,
} from "@/shared/browserTakeover.ts";
import type { BrowserConnectionEvent, BrowserConnectionAttempt } from "@/types/browserTakeover.ts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";

/** Hosts the API's shared private window and retains chrome for older API versions. */
export const BrowserControlDialog = ({
  url: initialUrl,
  opening = false,
  openingFailed = false,
  onRetry,
  onClose,
  onDone,
  onEnd,
  onTake,
  onRefresh,
  onConnection,
  mode = "control",
  footer,
  signInSheet,
  title = "Private browser",
  instruction = "Complete the step requested in the conversation, then choose Continue with okthx.",
}: {
  url: string | null;
  opening?: boolean;
  openingFailed?: boolean;
  onRetry?: () => void;
  onClose: () => void;
  onDone: () => Promise<unknown>;
  onEnd: () => Promise<unknown>;
  onTake?: () => Promise<unknown>;
  onRefresh?: (connection?: BrowserConnectionAttempt) => Promise<string>;
  onConnection?: (event: BrowserConnectionEvent) => void;
  mode?: "watch" | "control";
  footer: ReactNode;
  signInSheet?: ReactNode;
  title?: string;
  instruction?: string;
}) => {
  const keyboardInset = useKeyboardInset();
  const [replacement, setReplacement] = useState<{ initial: string; url: string }>();
  const url = replacement?.initial === initialUrl ? replacement.url : initialUrl;
  const frame = useRef<HTMLIFrameElement>(null);
  const pending = useRef(false);
  const refreshRequest = useRef(0);
  const [readyUrl, setReadyUrl] = useState<string>();
  const [shellFailedUrl, setShellFailedUrl] = useState<string>();
  const [recoveringUrl, setRecoveringUrl] = useState<string>();
  const expiredDocument = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (initialUrl !== null) return;
    setReadyUrl(undefined);
    setShellFailedUrl(undefined);
    setRecoveringUrl(undefined);
    setReplacement(undefined);
    expiredDocument.current = undefined;
    refreshRequest.current++;
  }, [initialUrl]);
  const unified = url !== null && isBrowserWindowUrl(url, getApiOrigin());
  const ready = unified && readyUrl === url;
  const shellFailed = url !== null && shellFailedUrl === url;
  const connectionCallbacks = useRef({ onRefresh, onConnection });
  useEffect(() => {
    connectionCallbacks.current = { onRefresh, onConnection };
  }, [onRefresh, onConnection]);
  useEffect(() => {
    if (!unified || !url || ready) return;
    const controller = new AbortController();
    const connection = browserWindowAttemptFromUrl(url);
    const timer = setTimeout(
      () => {
        expiredDocument.current = url;
        refreshRequest.current++;
        setRecoveringUrl(url);
        const { onRefresh, onConnection } = connectionCallbacks.current;
        if (!onRefresh) {
          setShellFailedUrl(url);
          return;
        }
        void recoverBrowserWindowDocument(connection, onRefresh, controller.signal, onConnection)
          .then((next) => {
            if (!controller.signal.aborted && initialUrl)
              setReplacement({ initial: initialUrl, url: next });
          })
          .catch(() => {
            if (!controller.signal.aborted) setShellFailedUrl(url);
          });
      },
      Math.max(0, Math.min(20000, connection.deadlineAtMs - Date.now())),
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [url, initialUrl, unified, ready]);
  useEffect(() => {
    if (unified && url && ready)
      frame.current?.contentWindow?.postMessage(
        { type: "okthx:browser-mode", mode },
        new URL(url).origin,
      );
  }, [mode, unified, url, ready]);
  useEffect(() => {
    if (!unified || !url) return;
    const origin = new URL(url).origin;
    const handleMessage = (event: MessageEvent) => {
      if (
        expiredDocument.current === url ||
        event.origin !== origin ||
        event.source !== frame.current?.contentWindow
      )
        return;
      const connectionEvent = browserConnectionEvent(event.data);
      if (connectionEvent) {
        onConnection?.(connectionEvent);
        return;
      }
      const action = browserWindowAction(event.data);
      if (browserWindowReady(event.data, url)) setReadyUrl(url);
      if (action === "close") onClose();
      if (action === "refresh" && !pending.current && onRefresh) {
        const request = ++refreshRequest.current;
        const source = frame.current?.contentWindow;
        void (async () => {
          try {
            const next = await onRefresh(browserWindowConnectionAttempt(event.data));
            if (request !== refreshRequest.current) return;
            source?.postMessage(
              {
                ...browserConnectionFromWindow(next, getApiOrigin()),
                requestId: event.data.requestId,
              },
              origin,
            );
          } catch {
            if (request !== refreshRequest.current) return;
            source?.postMessage(
              { type: "okthx:browser-window-result", requestId: event.data.requestId },
              origin,
            );
          }
        })();
        return;
      }
      if (action === "take" && !pending.current) {
        pending.current = true;
        refreshRequest.current++;
        const source = frame.current?.contentWindow;
        void (async () => {
          try {
            await onTake?.();
          } catch {
            source?.postMessage(
              { type: "okthx:browser-window-result", requestId: event.data.requestId },
              origin,
            );
          } finally {
            pending.current = false;
          }
        })();
        return;
      }
      if ((action !== "done" && action !== "end") || pending.current) return;
      pending.current = true;
      refreshRequest.current++;
      const source = frame.current?.contentWindow;
      void (async () => {
        try {
          await (action === "done" ? onDone() : onEnd());
        } catch {
          // Callback errors can contain private URLs; report only the fixed action context.
          Sentry.captureException(new Error("Private browser hand-back failed"), {
            tags: { action },
          });
          source?.postMessage(
            { type: "okthx:browser-window-result", requestId: event.data.requestId },
            origin,
          );
        } finally {
          pending.current = false;
        }
      })();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [url, unified, onClose, onDone, onEnd, onTake, onRefresh, onConnection]);
  return (
    <Dialog
      open={url !== null || opening || openingFailed}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        hideClose={ready && !signInSheet}
        style={{ height: `calc(100dvh - ${keyboardInset}px)` }}
        className="inset-0 h-dvh max-w-none translate-x-0 translate-y-0 flex flex-col gap-0 rounded-none border-0 p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <header className={ready ? "sr-only" : "shrink-0 border-b px-4 py-3 pr-12"}>
          <DialogTitle className="text-sm">{title}</DialogTitle>
          <DialogDescription className="mt-1 text-xs">{instruction}</DialogDescription>
        </header>
        {url !== null && !shellFailed && recoveringUrl !== url && (
          <iframe
            key={url}
            ref={frame}
            src={url}
            title="okthx’s browser"
            className="min-h-0 flex-1 w-full border-0 bg-popover"
            sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
            allow="autoplay; clipboard-read; clipboard-write"
            referrerPolicy="no-referrer"
            inert={!!signInSheet}
          />
        )}
        {url !== null && unified && !ready && !shellFailed && (
          <div
            role="status"
            className={cn(
              "absolute inset-x-0 top-20 flex flex-col items-center justify-center gap-3 bg-popover text-muted-foreground",
              signInSheet ? "bottom-1/2" : "bottom-0",
            )}
          >
            <LoaderCircle aria-hidden className="size-6 animate-spin" />
            <p>Connecting to your browser…</p>
          </div>
        )}
        {url === null && opening && (
          <div
            role="status"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-muted-foreground"
            style={{ paddingBottom: signInSheet ? "min(50dvh,26rem)" : undefined }}
          >
            <LoaderCircle aria-hidden className="size-6 animate-spin" />
            <p>Connecting to your browser…</p>
          </div>
        )}
        {((url === null && openingFailed && !opening) || shellFailed) && (
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4"
            style={{ paddingBottom: signInSheet ? "min(50dvh,26rem)" : undefined }}
          >
            <p role="alert">Couldn’t connect to your browser.</p>
            <Button data-statsig-name="browser-opening-retry" onClick={onRetry}>
              Retry
            </Button>
            <Button data-statsig-name="browser-opening-return" variant="ghost" onClick={onClose}>
              Return to task
            </Button>
          </div>
        )}
        {!unified && !signInSheet && footer}
        {signInSheet && (
          <section
            aria-label="Website sign-in"
            className="absolute inset-x-0 bottom-0 z-10 max-h-[min(85%,32rem)] overflow-y-auto overscroll-contain rounded-t-3xl border-t bg-popover px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-lg animate-in slide-in-from-bottom duration-200 motion-reduce:animate-none"
          >
            <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-border" />
            <div className="mx-auto max-w-sm">{signInSheet}</div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
};
